/* The referral loop (2026-10-07). Every page's public address is its referral code:
   keepsakepage.com/?ref=<slug>. The landing stores the code for 30 days; the create wizard sends it,
   and a referral counts as the beta invitation (no code needed). Counts live on the source page's
   event trail; organizers see their own numbers, the back office sees every source. */
import { supabase } from "./supabase";

const KEY = "kp_ref";
export const REF_TTL_MS = 30 * 24 * 3600e3;

export interface StoredRef { slug: string; at: number }

/* The link an organizer or contributor shares. Pure. */
export function refLinkFor(slug: string, origin = "https://keepsakepage.com"): string { return origin.replace(/\/$/, "") + "/?ref=" + encodeURIComponent(String(slug || "").toLowerCase()); }

/* The ?ref= value on a landing URL, normalized to a slug, or "". Pure. */
export function refFromSearch(search: string): string {
  const v = new URLSearchParams(search || "").get("ref") || "";
  const s = v.trim().toLowerCase();
  return /^[a-z0-9]([a-z0-9-]{0,38}[a-z0-9])?$/.test(s) ? s : "";
}

/* Is a stored referral still good? Pure. */
export function refIsFresh(r: StoredRef | null | undefined, now = Date.now()): boolean { return !!r && !!r.slug && now - r.at < REF_TTL_MS; }

export function storeRef(slug: string, now = Date.now()): void { try { localStorage.setItem(KEY, JSON.stringify({ slug, at: now })); } catch { /* private window */ } }
export function storedRef(now = Date.now()): string {
  try { const r = JSON.parse(localStorage.getItem(KEY) || "null") as StoredRef | null; return refIsFresh(r, now) ? r!.slug : ""; } catch { return ""; }
}
export function clearRef(): void { try { localStorage.removeItem(KEY); } catch { /* */ } }

/* Landing: remember a referral and count the visit (anon RPC, capped server-side). Returns the slug or "". */
export function captureRef(search: string): string {
  const slug = refFromSearch(search); if (!slug) return storedRef();
  storeRef(slug);
  supabase().rpc("referral_touch", { p_slug: slug }).then(() => undefined, () => undefined);
  return slug;
}

/* The share copy. Pure. */
export function referralShareText(honoree: string, link: string): string {
  const who = (honoree || "someone").trim();
  return "I just helped make a KeepSakePage for " + who + " — everyone who loves them leaves a video, a note or a photo, and it's cut into a trailer for the party. If you've got someone to celebrate, start one here (my link skips the waitlist): " + link;
}

export interface MyReferrals { visits30d: number; visits: number; signups: number; lastSignup?: string | null }
export async function myReferrals(slug: string): Promise<MyReferrals> { const r = await supabase().rpc("my_referrals", { p_slug: slug }); if (r.error) throw new Error(r.error.message); return r.data as MyReferrals; }
export interface ReferralSource { slug: string; honoree: string; visits: number; visits30d: number; signups: number; pages: { slug: string; honoree: string; at: string; status: string }[] }
export async function officeReferrals(): Promise<ReferralSource[]> { const r = await supabase().rpc("backoffice_referrals"); if (r.error) throw new Error(r.error.message); return (r.data || []) as ReferralSource[]; }
